import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import userServices from "services/userServices";
import { useNotification } from "providers/NotificationProvider";
import CreateTemplatePage from "./CreateTemplatePage";
import helpers from "utils/helpers";
import { useTranslation } from "context/TranslationProvider";

const CreateTemplate = () => {
  const { t } = useTranslation();
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { notify } = useNotification();

  const isEditMode = Boolean(templateId);

  const [loading, setLoading] = useState(false);
  const [placeholders, setPlaceholders] = useState({});
  const [templateData, setTemplateData] = useState({
    name: "",
    subject: "",
    content: "",
    type: "",
    pushTitle: "",
    pushBody: "",
    channels: ["EMAIL"],
    isActive: true,
  });

  const fetchTemplate = async () => {
    if (!isEditMode) return;

    try {
      setLoading(true);
      const res = await userServices.getTemplateById(templateId);
      const dataObj = res?.data || res;

      if (dataObj?._id) {
        setTemplateData({
          name: dataObj.name || "",
          subject: dataObj.subject || "",
          content: dataObj.content || "",
          pushTitle: dataObj.pushTitle || "",
          pushBody: dataObj.pushBody || "",
          type: dataObj.type || "",
          channels: dataObj.channels || ["EMAIL"],
          isActive: dataObj.isActive ?? true,
        });
      }
    } catch (err) {
      notify("error", t("page.notificationTemplates.toast.fetchFailed", "Failed to fetch templates"));
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaceholders = async () => {
    try {
      const res = await userServices.getPlaceholders();
      const placeholdersData = res?.data?.placeholders || res?.data || res?.placeholders || res;
      setPlaceholders(placeholdersData || {});
    } catch (err) {
      notify("error", t("page.notificationTemplates.toast.fetchFailed", "Failed to fetch templates"));
    }
  };

  const handleSubmit = async (data) => {
    try {

      if (
        !data.name?.trim() ||
        !data.subject?.trim() ||
        !data.content ||
        data.content === "<p><br></p>" ||
        !data.channels ||
        data.channels.length === 0
      ) {
        notify("warning", t("page.notificationTemplates.toast.allFieldsRequired", "All fields are required."));
        return;
      }

      setLoading(true);

      if (isEditMode) {
        const res = await userServices.updateTemplate(templateId, data);
        if (res?.error) {
          notify("error", t(res.messageKey || res.error, res.message || "Error"));
          return;
        }
        notify("success", t(res?.messageKey || "page.notificationTemplates.toast.updatedSuccessfully", res?.message || t("page.notificationTemplates.toast.updatedSuccessfully", "Template updated successfully")));
        navigate(`/admin/notification-templates`);
      } else {
        const res = await userServices.createTemplate(data);
        const templateObj = res?.data?.template || res?.data || res?.template || res;

        if (res?.error) {
          notify("error", t(res.messageKey || res.error, res.message || "Error"));
          return;
        }

        if (templateObj?._id) {
          notify("success", t(res?.messageKey || "page.notificationTemplates.toast.createdSuccessfully", res?.message || t("page.notificationTemplates.toast.createdSuccessfully", "Template created successfully")));
          navigate(`/admin/notification-templates`);
          return;
        }
      }

    } catch (err) {
      notify("error", t(err?.messageKey || err?.message || "Error"));
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    try {
      const res = await userServices.previewTemplate({
        subject: templateData.subject,
        content: templateData.content,
      });

      return res?.data?.html || res?.html || null;
    } catch (err) {
      notify("error", t("page.notificationTemplates.toast.previewFailed", "Failed to preview email template"));
      return null;
    }
  };

  useEffect(() => {
    fetchTemplate();
    fetchPlaceholders();
    // eslint-disable-next-line
  }, [templateId]);

  return (
    <CreateTemplatePage
      loading={loading}
      templateData={templateData}
      setTemplateData={setTemplateData}
      placeholders={placeholders}
      isEditMode={isEditMode}
      onSubmit={handleSubmit}
      onPreview={handlePreview}
      templateTypes={helpers.TEMPLATE_TYPES}
    />
  );
};

export default CreateTemplate;